import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { MatSnackBar } from '@angular/material';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private lastSessionToastAt = 0;

  constructor(
    private readonly authService: AuthService,
    private readonly snackBar: MatSnackBar
  ) { }

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.shouldAttachToken(req.url)) {
      return next.handle(req);
    }

    return this.authService.acquireAccessToken().pipe(
      switchMap((token) => this.forwardWithToken(req, next, token)),
      catchError((error) => this.handleAuthFailure(req, next, error))
    );
  }

  private forwardWithToken(req: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    if (!token) {
      return throwError(() => 'Empty auth token');
    }
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
    return next.handle(authReq);
  }

  private handleAuthFailure(req: HttpRequest<any>, next: HttpHandler, error: any): Observable<HttpEvent<any>> {
    if (this.isInteractionRequiredError(error)) {
      this.notifyAndLogin();
      return throwError(() => error);
    }

    if (!this.isUnauthorizedApiError(error) && !this.isCorsMaskedAuthFailure(error)) {
      return throwError(() => error);
    }

    // Retry once with a fresh silent token before forcing interactive login.
    return this.authService.refreshTokenIfNeeded().pipe(
      switchMap((token) => this.forwardWithToken(req, next, token)),
      catchError((refreshError) => {
        if (this.isInteractionRequiredError(refreshError)
          || this.isUnauthorizedApiError(refreshError)
          || this.isCorsMaskedAuthFailure(refreshError)) {
          this.notifyAndLogin();
        }
        return throwError(() => refreshError);
      })
    );
  }

  private notifyAndLogin(): void {
    const now = Date.now();
    // Avoid showing duplicate toasts when multiple API calls fail together.
    if (now - this.lastSessionToastAt > 5000) {
      this.lastSessionToastAt = now;
      this.snackBar.open('Session expired, redirecting to login', 'Dismiss', { duration: 3000 });
    }
    this.authService.login();
  }

  private isInteractionRequiredError(error: any): boolean {
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }
    const errorCode = ((error && (error.errorCode || error.code)) || '').toLowerCase();
    return errorCode === 'interaction_required'
      || errorCode === 'login_required'
      || errorCode === 'consent_required'
      || errorCode === 'no_tokens_found';
  }

  private isUnauthorizedApiError(error: any): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  private isCorsMaskedAuthFailure(error: any): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 0) {
      return false;
    }
    // A status-0 response means the browser blocked the response (no CORS headers).
    // This happens for genuine auth failures (gateway strips CORS on 401) but also
    // for rate-limiting (gateway strips CORS on 429). The only safe way to tell them
    // apart: if the user already has a valid MSAL session, a status-0 is almost
    // certainly NOT an auth failure — it is a transient network/rate-limit error.
    // Only treat status-0 as an auth failure when there is no active account, i.e.
    // the user is genuinely unauthenticated.
    if (this.authService.isLoggedIn()) {
      return false;
    }
    return true;
  }

  private shouldAttachToken(url: string): boolean {
    const lowerUrl = (url || '').toLowerCase();
    const isStaticRequest = lowerUrl.indexOf('/assets/') > -1
      || lowerUrl.indexOf('/i18n/') > -1
      || lowerUrl.indexOf('manifest.json') > -1
      || lowerUrl.indexOf('ngsw-worker.js') > -1;
    if (isStaticRequest) {
      return false;
    }

    const isAbsoluteHttp = lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://');
    const isApiPath = lowerUrl.indexOf('/apis/') > -1
      || lowerUrl.indexOf('/adminportalapis/') > -1
      || lowerUrl.startsWith('/v')
      || lowerUrl.startsWith('/reports')
      || lowerUrl.startsWith('/products')
      || lowerUrl.startsWith('/subscription')
      || lowerUrl.startsWith('/authcode');
    return isApiPath
      || (isAbsoluteHttp && lowerUrl.indexOf('/apis/') > -1)
      || (isAbsoluteHttp && lowerUrl.indexOf('/adminportalapis/') > -1);
  }
}
